import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Rating,
  Pagination,
  CircularProgress,
  alpha,
} from "@mui/material";
import {
  Add,
  Search,
  MoreVert,
  Visibility,
  Edit,
  Business,
  Phone,
  Email,
  Inventory,
  Analytics,
  Folder,
  Payment,
  Chat,
  AccountBalance,
  SwapHoriz
} from "@mui/icons-material";
import { suppliersAPI } from "../../services/API";
import { useSnackbar } from "notistack";
import { useAuth } from '../../../../INV/pharmecy/contexts/AuthContext';

export const InvSuppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { hasAnyRole } = useAuth();

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 9,
    total: 0,
    pages: 0,
  });

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSuppliers();
    }, 500);

    return () => clearTimeout(timer);
  }, [pagination.page, pagination.limit, searchTerm]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
      };

      const response = await suppliersAPI.getAll(params);
      const { suppliers: suppliersData, pagination: paginationData } =
        response.data.data;

      setSuppliers(suppliersData);
      setPagination((prev) => ({
        ...prev,
        total: paginationData.total,
        pages: paginationData.pages,
      }));
    } catch (error) {
      enqueueSnackbar("Error fetching suppliers", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, page: value }));
  };

  const handleMenuOpen = (event, supplier) => {
    setAnchorEl(event.currentTarget);
    setSelectedSupplier(supplier);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSupplier(null);
  };

  const handleView = () => {
    if (selectedSupplier) {
      navigate(`/hospital/hospital-inventory/suppliers/${selectedSupplier._id}`);
    }
    handleMenuClose();
  };

  const handleEdit = () => {
    if (selectedSupplier) {
      navigate(`/hospital/hospital-inventory/suppliers/${selectedSupplier._id}/edit`);
    }
    handleMenuClose();
  };

  const handleSupplierProducts = () => {
    if (selectedSupplier) {
      navigate(`/hospital/hospital-inventory/suppliers/${selectedSupplier._id}/products`);
    }
    handleMenuClose();
  };

  const handleSupplierAnalytics = () => {
    if (selectedSupplier) {
      navigate(`/hospital/hospital-inventory/suppliers/${selectedSupplier._id}/analytics`);
    }
    handleMenuClose();
  };

  const handleSupplierDocuments = () => {
    if (selectedSupplier) {
      navigate(`/hospital/hospital-inventory/suppliers/${selectedSupplier._id}/documents`);
    }
    handleMenuClose();
  };

  const handleSupplierPayments = () => {
    if (selectedSupplier) {
      navigate(`/hospital/hospital-inventory/suppliers/${selectedSupplier._id}/payments`);
    }
    handleMenuClose();
  };

  const handleSupplierCommunications = () => {
    if (selectedSupplier) {
      navigate(`/hospital/hospital-inventory/suppliers/${selectedSupplier._id}/communications`);
    }
    handleMenuClose();
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase();
  };

  const handleSupplierFinancials = () => {
    if (selectedSupplier) {
      navigate(`/hospital/hospital-inventory/suppliers/${selectedSupplier._id}/financials`);
    }
    handleMenuClose();
  };

  const handleSupplierReturns = () => {
    if (selectedSupplier) {
      navigate(`/hospital/hospital-inventory/suppliers/${selectedSupplier._id}/returns`);
    }
    handleMenuClose();
  };

  const getPaymentTermsColor = (terms) => {
    const colors = {
      advance: "success",
      "7_days": "info",
      "15_days": "primary",
      "30_days": "warning",
      "45_days": "warning",
      "60_days": "error",
    };
    return colors[terms] || "default";
  };

  return (
    <Box  
      sx={{
        minHeight:'100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          p: 3,
          backgroundColor: 'background.paper',
          borderRadius: 3,
          boxShadow: 1,
        }}
      >
        <Box>
          <Typography 
            variant="h3" 
            fontWeight="bold" 
            sx={{ 
              fontSize: { xs: '2rem', md: '2.5rem' },
              background: 'linear-gradient(45deg, #1976d2, #00bcd4)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              mb: 1
            }}
          >
            Suppliers Management
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              fontSize: '1.1rem',
              color: 'text.secondary',
              fontWeight: 400
            }}
          >
            Manage your suppliers and vendor relationships
          </Typography>
        </Box>
        {hasAnyRole(["admin", "inventory_manager"]) && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/hospital/hospital-inventory/suppliers/new")}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 3,
              boxShadow: 3,
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Add Supplier
          </Button>
        )}
      </Box>

      {/* Search Card */}
      <Card 
        sx={{ 
          mb: 4, 
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          boxShadow: 3,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search suppliers by name, contact person, or email..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                InputProps={{
                  sx: {
                    fontSize: '1.1rem',
                    borderRadius: 3,
                    backgroundColor: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 28, color: 'primary.main' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: { md: 'right' } }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontSize: '1.3rem',
                    fontWeight: 600,
                    color: 'primary.main'
                  }}
                >
                  {pagination.total} Suppliers
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: '1rem',
                    color: 'text.secondary',
                    mt: 0.5
                  }}
                >
                  Total registered
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Suppliers Grid */}
      {loading ? (
        <Box sx={{ 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center", 
          py: 8,
          minHeight: 400 
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} thickness={4} />
            <Typography 
              variant="h6" 
              sx={{ 
                mt: 2,
                fontSize: '1.2rem',
                color: 'text.secondary'
              }}
            >
              Loading Suppliers...
            </Typography>
          </Box>
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            {suppliers.map((supplier) => (
              <Grid item xs={12} sm={6} lg={4} key={supplier._id}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    borderRadius: 4,
                    border: `1px solid ${alpha('#1976d2', 0.1)}`,
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                    "&:hover": { 
                      transform: "translateY(-8px)", 
                      boxShadow: 8,
                      border: `1px solid ${alpha('#1976d2', 0.3)}`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Header with Avatar and Menu */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 3,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: "primary.main",
                            width: 64,
                            height: 64,
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            boxShadow: 3,
                          }}
                        >
                          {getInitials(supplier.name)}
                        </Avatar>
                        <Box sx={{ maxWidth: 'calc(100% - 80px)' }}>
                          <Typography 
                            variant="h5" 
                            fontWeight="bold"
                            sx={{
                              fontSize: '1.4rem',
                              lineHeight: 1.2,
                              mb: 0.5
                            }}
                          >
                            {supplier.name}
                          </Typography>
                          <Typography 
                            variant="body1" 
                            sx={{
                              fontSize: '1rem',
                              color: 'text.secondary',
                              fontWeight: 500,
                            }}
                          >
                            {supplier.contactPerson}
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton
                        size="large"
                        onClick={(e) => handleMenuOpen(e, supplier)}
                        sx={{
                          backgroundColor: 'action.hover',
                          '&:hover': {
                            backgroundColor: 'action.selected',
                          }
                        }}
                      >
                        <MoreVert sx={{ fontSize: 28 }} />
                      </IconButton>
                    </Box>

                    {/* Contact Information */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        <Phone sx={{ fontSize: 24, color: "primary.main" }} />
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500 }}>
                          {supplier.phone}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        <Email sx={{ fontSize: 24, color: "primary.main" }} />
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontSize: '1.1rem',
                            fontWeight: 500,
                            wordBreak: 'break-word'
                          }}
                        >
                          {supplier.email}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Rating and Lead Time */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                        p: 2,
                        backgroundColor: alpha('#1976d2', 0.03),
                        borderRadius: 3,
                      }}
                    >
                      <Box>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontSize: '1rem',
                            color: 'text.secondary',
                            mb: 1,
                            fontWeight: 500
                          }}
                        >
                          Performance Rating
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Rating 
                            value={supplier.rating} 
                            size="large" 
                            readOnly 
                            sx={{ fontSize: '1.4rem' }}
                          />
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontSize: '1.1rem',
                              fontWeight: 600,
                              color: 'primary.main'
                            }}
                          >
                            {supplier.rating}/5
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontSize: '1rem',
                            color: 'text.secondary',
                            mb: 1,
                            fontWeight: 500
                          }}
                        >
                          Lead Time
                        </Typography>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontSize: '1.3rem',
                            fontWeight: 700,
                            color: 'secondary.main'
                          }}
                        >
                          {supplier.leadTime} days
                        </Typography>
                      </Box>
                    </Box>

                    {/* Status Chips */}
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                      <Chip
                        label={supplier.paymentTerms.replace("_", " ")}
                        size="medium"
                        color={getPaymentTermsColor(supplier.paymentTerms)}
                        sx={{ 
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          height: 32,
                        }}
                      />
                      <Chip
                        label={supplier.isActive ? "Active" : "Inactive"}
                        size="medium"
                        color={supplier.isActive ? "success" : "default"}
                        variant={supplier.isActive ? "filled" : "outlined"}
                        sx={{ 
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          height: 32,
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* No Results */}
          {suppliers.length === 0 && !loading && (
            <Card 
              sx={{ 
                textAlign: "center", 
                py: 8, 
                borderRadius: 4,
                boxShadow: 3,
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
              }}
            >
              <CardContent>
                <Business
                  sx={{ 
                    fontSize: 80, 
                    color: "primary.main", 
                    mb: 3,
                    opacity: 0.7
                  }}
                />
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: '1.8rem',
                    color: "text.primary",
                    fontWeight: 600,
                    mb: 2
                  }}
                >
                  No suppliers found
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ 
                    fontSize: '1.2rem',
                    color: "text.secondary",
                    mb: 4,
                    fontWeight: 400
                  }}
                >
                  {searchTerm
                    ? "Try adjusting your search terms or filters"
                    : "Get started by adding your first supplier to the system"}
                </Typography>
                {hasAnyRole(["admin", "inventory_manager"]) && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => navigate("/hospital/hospital-inventory/suppliers/new")}
                    sx={{
                      px: 5,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderRadius: 3,
                      boxShadow: 3,
                    }}
                  >
                    Add First Supplier
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 6, mb: 2 }}>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: '1.1rem',
                    margin: '0 4px',
                    minWidth: 45,
                    height: 45,
                  },
                  '& .MuiPaginationItem-page.Mui-selected': {
                    fontWeight: 'bold',
                    boxShadow: 2,
                  }
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* Supplier Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            width: 280,
            maxHeight: 500,
            borderRadius: 3,
            boxShadow: 8,
            mt: 1,
            '& .MuiMenuItem-root': {
              fontSize: '1.1rem',
              py: 1.5,
              px: 2,
            }
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleView}>
          <Visibility sx={{ mr: 2, fontSize: 24 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleSupplierFinancials}>
          <AccountBalance sx={{ mr: 2, fontSize: 24 }} />
          Financial Overview
        </MenuItem>

        {hasAnyRole(["admin", "inventory_manager"]) && (
          <MenuItem onClick={handleEdit}>
            <Edit sx={{ mr: 2, fontSize: 24 }} />
            Edit Supplier
          </MenuItem>
        )}

        <MenuItem onClick={handleSupplierProducts}>
          <Inventory sx={{ mr: 2, fontSize: 24 }} />
          Product Catalog
        </MenuItem>
        <MenuItem onClick={handleSupplierReturns}>
          <SwapHoriz sx={{ mr: 2, fontSize: 24 }} />
          Returns & Exchanges
        </MenuItem>

        <MenuItem onClick={handleSupplierAnalytics}>
          <Analytics sx={{ mr: 2, fontSize: 24 }} />
          Performance Analytics
        </MenuItem>

        <MenuItem onClick={handleSupplierCommunications}>
          <Chat sx={{ mr: 2, fontSize: 24 }} />
          Communication History
        </MenuItem>

        <MenuItem onClick={handleSupplierDocuments}>
          <Folder sx={{ mr: 2, fontSize: 24 }} />
          Documents & Files
        </MenuItem>

        <MenuItem onClick={handleSupplierPayments}>
          <Payment sx={{ mr: 2, fontSize: 24 }} />
          Payment History
        </MenuItem>
      </Menu>
    </Box>
    </Box>
  );
};